import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Button } from '@mono/ui-components';
import { IconMaximize as Maximize, IconMoon as Moon, IconArrowsMove as Move, IconSun as Sun, IconZoomIn as ZoomIn, IconZoomOut as ZoomOut } from '@tabler/icons-react';
import { memo, useRef } from 'react';
import { MermaidDiagram } from './MermaidDiagram';
import { exportMermaidRaster } from './mermaidOfficeExport';
export const MermaidViewerDialog = /*#__PURE__*/ memo(({ code, theme, setTheme, scale, setScale, pan, setPan, diagramSize, viewportRef, fitToScreen, resetView, download, beginDrag, onDrag, endDrag })=>{
    const modalDiagramRef = useRef(null);
    const exportRaster = async (format)=>{
        const svgElement = modalDiagramRef.current?.querySelector('svg');
        if (!svgElement) return;
        try {
            await exportMermaidRaster(svgElement, format, {
                backgroundColor: theme === 'dark' ? '#1a1a1a' : '#ffffff',
                size: diagramSize ?? {
                    width: 1200,
                    height: 800
                }
            });
        } catch  {
        // Same as PNG download: fail closed if the SVG is not capturable.
        }
    };
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center justify-between gap-2 mb-2",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex gap-1",
                        children: [
                            /*#__PURE__*/ _jsx(Button, {
                                size: "icon",
                                variant: "outline",
                                className: "h-8 w-8",
                                onClick: ()=>setScale((s)=>Math.max(0.25, s - 0.25)),
                                children: /*#__PURE__*/ _jsx(ZoomOut, {
                                    className: "h-4 w-4"
                                })
                            }),
                            /*#__PURE__*/ _jsx(Button, {
                                size: "icon",
                                variant: "outline",
                                className: "h-8 w-8",
                                onClick: ()=>setScale((s)=>Math.min(4, s + 0.25)),
                                children: /*#__PURE__*/ _jsx(ZoomIn, {
                                    className: "h-4 w-4"
                                })
                            }),
                            /*#__PURE__*/ _jsx(Button, {
                                size: "icon",
                                variant: "outline",
                                className: "h-8 w-8",
                                onClick: fitToScreen,
                                "aria-label": "Fit to screen",
                                children: /*#__PURE__*/ _jsx(Maximize, {
                                    className: "h-4 w-4"
                                })
                            }),
                            /*#__PURE__*/ _jsx(Button, {
                                size: "icon",
                                variant: "outline",
                                className: "h-8 w-8",
                                onClick: resetView,
                                "aria-label": "Reset view",
                                children: /*#__PURE__*/ _jsx(Move, {
                                    className: "h-4 w-4"
                                })
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex gap-1",
                        children: [
                            /*#__PURE__*/ _jsx(Button, {
                                size: "sm",
                                variant: "outline",
                                onClick: ()=>setTheme(theme === 'dark' ? 'default' : 'dark'),
                                "aria-label": "Toggle theme",
                                className: "px-2",
                                children: theme === 'dark' ? /*#__PURE__*/ _jsx(Sun, {
                                    className: "h-4 w-4"
                                }) : /*#__PURE__*/ _jsx(Moon, {
                                    className: "h-4 w-4"
                                })
                            }),
                            /*#__PURE__*/ _jsx(Button, {
                                size: "sm",
                                variant: "outline",
                                onClick: ()=>download('svg'),
                                children: "Download SVG"
                            }),
                            /*#__PURE__*/ _jsx(Button, {
                                size: "sm",
                                variant: "outline",
                                onClick: ()=>void exportRaster('png'),
                                children: "Download PNG"
                            }),
                            /*#__PURE__*/ _jsx(Button, {
                                size: "sm",
                                variant: "outline",
                                onClick: ()=>void exportRaster('pptx'),
                                children: "PowerPoint"
                            }),
                            /*#__PURE__*/ _jsx(Button, {
                                size: "sm",
                                variant: "outline",
                                onClick: ()=>void exportRaster('docx'),
                                children: "Word"
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "relative h-[calc(85vh-120px)] border rounded-md bg-background",
                children: [
                    /*#__PURE__*/ _jsx("section", {
                        className: "absolute inset-0 overflow-auto",
                        "aria-label": "Diagram viewport",
                        ref: viewportRef,
                        children: /*#__PURE__*/ _jsx("div", {
                            className: "min-w-[1200px] min-h-[800px] cursor-grab",
                            onMouseDown: beginDrag,
                            onMouseMove: onDrag,
                            onMouseUp: endDrag,
                            onMouseLeave: endDrag,
                            role: "application",
                            "aria-label": "Diagram canvas",
                            onWheel: (e)=>{
                                if (e.ctrlKey || e.metaKey) {
                                    e.preventDefault();
                                    const rect = e.currentTarget.getBoundingClientRect();
                                    const focusX = e.clientX - rect.left;
                                    const focusY = e.clientY - rect.top;
                                    const old = scale;
                                    const dz = e.deltaY > 0 ? -0.1 : 0.1;
                                    const next = Math.min(4, Math.max(0.25, old + dz));
                                    const dx = focusX - pan.x;
                                    const dy = focusY - pan.y;
                                    const k = next / old;
                                    setScale(next);
                                    setPan({
                                        x: focusX - dx * k,
                                        y: focusY - dy * k
                                    });
                                }
                            },
                            style: {
                                width: diagramSize?.width ?? 1200,
                                height: diagramSize?.height ?? 800,
                                transform: `translate(${pan.x}px, ${pan.y}px) scale(${scale})`,
                                transformOrigin: '0 0'
                            },
                            children: /*#__PURE__*/ _jsx("div", {
                                ref: modalDiagramRef,
                                children: /*#__PURE__*/ _jsx(MermaidDiagram, {
                                    code: code,
                                    theme: theme
                                })
                            })
                        })
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "absolute bottom-2 right-2 bg-background/80 backdrop-blur border rounded p-1 shadow text-[10px]",
                        children: /*#__PURE__*/ _jsxs("div", {
                            className: "w-40 h-24 overflow-hidden border rounded relative",
                            children: [
                                /*#__PURE__*/ _jsx("div", {
                                    className: "scale-[0.15] origin-top-left",
                                    children: /*#__PURE__*/ _jsx(MermaidDiagram, {
                                        code: code,
                                        theme: theme
                                    })
                                }),
                                diagramSize && /*#__PURE__*/ _jsxs(_Fragment, {
                                    children: [
                                        /*#__PURE__*/ _jsx("div", {
                                            className: "absolute border border-primary/60 bg-primary/10",
                                            style: {
                                                left: `${Math.max(0, -pan.x * 0.15 / scale)}px`,
                                                top: `${Math.max(0, -pan.y * 0.15 / scale)}px`,
                                                width: `${(viewportRef.current?.clientWidth || 0) * (0.15 / scale)}px`,
                                                height: `${(viewportRef.current?.clientHeight || 0) * (0.15 / scale)}px`
                                            }
                                        }),
                                        /*#__PURE__*/ _jsx("button", {
                                            type: "button",
                                            className: "absolute inset-0 z-10 opacity-0",
                                            "aria-label": "Pan via minimap",
                                            onClick: (e)=>{
                                                const box = e.currentTarget.getBoundingClientRect();
                                                const clickX = e.clientX - box.left;
                                                const clickY = e.clientY - box.top;
                                                const targetX = (viewportRef.current?.clientWidth || 0) / 2 - clickX / 0.15 * scale;
                                                const targetY = (viewportRef.current?.clientHeight || 0) / 2 - clickY / 0.15 * scale;
                                                setPan({
                                                    x: targetX,
                                                    y: targetY
                                                });
                                            }
                                        })
                                    ]
                                })
                            ]
                        })
                    })
                ]
            })
        ]
    });
});
MermaidViewerDialog.displayName = 'MermaidViewerDialog';
